import qs from 'qs';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import SVG from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Navigate, useLocation } from 'react-router-dom';

import FeaturedAdverts from '@Components/Advert/FeaturedAdverts';
import FeaturedTabAdverts from '@Components/Advert/FeaturedTabAdverts';
import FeaturedAuctions from '@Components/Auction/FeaturedAuctions';
import Container from '@Components/layout/Container';
import Layout from '@Components/layout/Layout';
import Row from '@Components/layout/Row';
import { PaperBodySpacing } from '@Components/PaperBody';
import PageSection from '@Components/sections/PageSection';
import Typography from '@Components/Typography';
import config from '@Config/config';
import { FEATURED_FETCH_LIMIT } from '@Config/constants';
import { Messages } from '@Config/messages';
import { DEFAULT_HOMEPAGE, RouteConfig } from '@Config/routes';
import { CategoryKind } from '@Graphql/graphqlTypes.generated';
import { useRouteParams } from '@Hooks/useRouteParams';
import { useSetAppKind } from '@Hooks/useSetAppKind';
import { useTranslateObject } from '@Hooks/useTranslateObject';
import { useTranslations } from '@Hooks/useTranslations';
import { ReactComponent as VestIcon } from '@Images/icons/vest.svg';
import { ReactComponent as WateringCanIcon } from '@Images/icons/watering-can.svg';
import { getAppConfig, getFeatureToggle } from '@Store/app/app.selectors';
import { locationChange } from '@Store/navigation/navigation.actions';
import { useAdvertFilterData } from '@Utils/hooks/useAdvertFilterData';
import { buildCategoryPageUrl } from '@Utils/router';

import HeroSection from './HeroSection/HeroSection';
import styles from './IntroPage.module.scss';
import NewsletterSection from './NewsletterSection';
import PartnersSection from './PartnersSection';

const isCategoryKind = (value: string): value is CategoryKind => {
  return Object.values(CategoryKind).includes(value as CategoryKind);
};

const CategoryItem = ({ url, title, icon }: { url: string; title: string; icon: ReactNode }) => {
  return (
    <Link to={url} className={styles.categoryItem}>
      {icon && <div className={styles.categoryImage}>{icon}</div>}
      <Typography msg={title} tag="h3" className={styles.categoryTitle} />
    </Link>
  );
};

const IntroPage: React.FunctionComponent = () => {
  const t = useTranslations();
  const auctionsEnabled = useSelector(getFeatureToggle('auction'));

  const { appKind: appKindFromRoute } = useRouteParams(RouteConfig.Home);
  const location = useLocation();
  const searchData = qs.parse(location.search, { ignoreQueryPrefix: true });
  const [isTabAdvertsComplete, setIsTabAdvertsComplete] = useState(false);
  const [isFeaturedAdvertsComplete, setIsFeaturedAdvertsComplete] = useState(false);
  const [isAuctionsComplete, setIsAuctionsComplete] = useState(!auctionsEnabled);

  const { buildMarketPlaceFiltersQueryUrl } = useAdvertFilterData();
  const dispatch = useDispatch();

  const scrollRef = useRef<HTMLDivElement>(null);
  const appKind = appKindFromRoute.toUpperCase();

  const isAgro = appKind === CategoryKind.Agro;

  useEffect(() => {
    if (!config.CONSTRUCTIONS_ENABLED && appKind === CategoryKind.Constructions) {
      dispatch(locationChange({ path: RouteConfig.Home.buildLink({ appKind: 'agro' }) }));
    }
  }, [appKindFromRoute]);

  const appConfig = useSelector(getAppConfig);

  const categoriesFiltered = appConfig.parentCategories
    .filter(category => category.kind === appKind)
    .sort((a, b) => a.orderNum - b.orderNum);

  const selectTranslation = useTranslateObject();

  const featuredCategory = categoriesFiltered.at(0);

  useEffect(() => {
    if (searchData?.subscribe === 'true' && isTabAdvertsComplete && isFeaturedAdvertsComplete && isAuctionsComplete) {
      scrollRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [searchData, isTabAdvertsComplete, isFeaturedAdvertsComplete, isAuctionsComplete]);

  useSetAppKind(appKind as CategoryKind);

  if (!isCategoryKind(appKind)) {
    return <Navigate to={DEFAULT_HOMEPAGE} replace />;
  }

  return (
    <Layout className={styles.container}>
      <Container className={styles.container}>
        <HeroSection onSubmit={values => dispatch(locationChange({ path: buildMarketPlaceFiltersQueryUrl(values) }))} />
        <FeaturedTabAdverts onCompleted={() => setIsTabAdvertsComplete(true)} />
        <PageSection title={t('msg_landing_popular_cats_title')} spacing={PaperBodySpacing.s}>
          <Row className={styles.categoriesList}>
            {categoriesFiltered.map(category => {
              return (
                <CategoryItem
                  key={category.slug}
                  url={buildCategoryPageUrl(category.slug)}
                  title={selectTranslation(category.name)}
                  icon={category.icon ? <SVG src={category.icon} /> : null}
                />
              );
            })}
            <CategoryItem
              url={RouteConfig.Marketplace.buildLink()}
              title={t('msg_other_ads_category_title')}
              icon={isAgro ? <WateringCanIcon /> : <VestIcon />}
            />
          </Row>
        </PageSection>
        <div className={styles.mainContainer}>
          <PageSection title={selectTranslation(featuredCategory?.name)} spacing={PaperBodySpacing.none}>
            <FeaturedAdverts
              featuredCategory={featuredCategory}
              itemsCount={FEATURED_FETCH_LIMIT * 2}
              onCompleted={() => setIsFeaturedAdvertsComplete(true)}
            />
          </PageSection>
          {auctionsEnabled && (
            <PageSection title={Messages.landingFeaturedAuctionsTitle} spacing={PaperBodySpacing.none}>
              <FeaturedAuctions itemsCount={FEATURED_FETCH_LIMIT} onCompleted={() => setIsAuctionsComplete(true)} />
            </PageSection>
          )}
        </div>
        <PartnersSection />
        <NewsletterSection scrollRef={scrollRef} />
      </Container>
    </Layout>
  );
};

export default IntroPage;
